import { FeedstockBmpPotentialDirect } from './feedstock-bmp-potential-type-models/feedstock-bmp-potential-direct.model';
import { FeedstockBmpCurve } from './feedstock-bmp-potential-type-models/feedstock-bmp-curve.model';
import { FeedstockBmpPotentialCod } from './feedstock-bmp-potential-type-models/feedstock-bmp-potential-cod.model';
import { FeedstockBmpPotentialYield } from './feedstock-bmp-potential-type-models/feedstock-bmp-potential-yield.model';
import { FeedstockBmpPotentialNutrient } from './feedstock-bmp-potential-type-models/feedstock-bmp-potential-nutrient.model';
import { FeedstockBmpPotentialElemental } from './feedstock-bmp-potential-type-models/feedstock-bmp-potential-elemental.model';
import { FeedstockBmpPotentialAI2 } from './feedstock-bmp-potential-type-models/feedstock-bmp-potential-AI2.model';
import { FeedstockBmpPotentialAI1 } from './feedstock-bmp-potential-type-models/feedstock-bmp-potential-AI1.model';

import { InputSummary, InputSummaryItem } from '../../../input-summary';
import InputSummaryUtils from '../../../../helper/input-summary/input-summary-util';


/**
 * An interface used to define the class structure for the feedstock bmp potential
 */
export interface FeedstockBmpPotentialInterface {
  type: string;
  bmpDirect: FeedstockBmpPotentialDirect;
  bmpCurve: FeedstockBmpCurve;
  bmpYield: FeedstockBmpPotentialYield;
  bmpCod: FeedstockBmpPotentialCod;
  bmpNutrient: FeedstockBmpPotentialNutrient;
  bmpElemental: FeedstockBmpPotentialElemental;
  bmpAI1: FeedstockBmpPotentialAI1;
  bmpAI2: FeedstockBmpPotentialAI2;
}

/**
 * A class that contains members for the feedstock BMP potential
 */
export class FeedstockBmpPotential {
  type: string;
  bmpDirect: FeedstockBmpPotentialDirect;
  bmpCurve: FeedstockBmpCurve;
  bmpYield: FeedstockBmpPotentialYield;
  bmpCod: FeedstockBmpPotentialCod;
  bmpNutrient: FeedstockBmpPotentialNutrient;
  bmpElemental: FeedstockBmpPotentialElemental;
  bmpAI1: FeedstockBmpPotentialAI1;
  bmpAI2: FeedstockBmpPotentialAI2;

  /**
   * constructs the FeedstockBMPPotential class.
   * @param param is used to construct the class. If null or not provided, the class will be set to default values
   */
  constructor(param: FeedstockBmpPotentialInterface = {
    type: null,
    bmpDirect: new FeedstockBmpPotentialDirect(),
    bmpCurve: new FeedstockBmpCurve(),
    bmpYield: new FeedstockBmpPotentialYield(),
    bmpCod: new FeedstockBmpPotentialCod(),
    bmpNutrient: new FeedstockBmpPotentialNutrient(),
    bmpElemental: new FeedstockBmpPotentialElemental(),
    bmpAI1: new FeedstockBmpPotentialAI1(),
    bmpAI2: new FeedstockBmpPotentialAI2()
  }) {
    this.type = param.type ? param.type : null;
    this.bmpDirect = new FeedstockBmpPotentialDirect(param.bmpDirect);
    this.bmpCurve = new FeedstockBmpCurve(param.bmpCurve);
    this.bmpYield = new FeedstockBmpPotentialYield(param.bmpYield);
    this.bmpCod = new FeedstockBmpPotentialCod(param.bmpCod);
    this.bmpNutrient = new FeedstockBmpPotentialNutrient(param.bmpNutrient);
    this.bmpElemental = new FeedstockBmpPotentialElemental(param.bmpElemental);
    this.bmpAI1 = new FeedstockBmpPotentialAI1(param.bmpAI1);
    this.bmpAI2 = new FeedstockBmpPotentialAI2(param.bmpAI2);
  }

  /**
   * returns a separate instance of the provided FeedstockBmpPotential obj
   * @param feedstockBmpPotential will be returned as a separate instance
   */
  static deepCopy(feedstockBmpPotential: FeedstockBmpPotential) {
    let copy;
    if (feedstockBmpPotential) {
      copy = new FeedstockBmpPotential(feedstockBmpPotential);
      return copy;
    }
    throw new Error('unable to perform deep copy of FeedstockBmpPotential');
  }

  /**
   * Returns an InputSummary of the FeedstockBmpPotential. The included inputs depend on the model type
   */
  getInputSummary(path: string[]) {
    const inputSummary = new InputSummary();
    inputSummary.inputs.push(... this.getSummaryInputs(path));
    switch (this.type) {
      case FeedstockBmpType.BMP_DIRECT:
        inputSummary.inputs.push(... this.bmpDirect.getSummaryInputs([...path, 'bmpDirect']));
        break;
      case FeedstockBmpType.BMP_CURVE:
        inputSummary.inputs.push(... this.bmpCurve.getSummaryInputs([...path, 'bmpCurve']));
        break;
      case FeedstockBmpType.BIOGAS_YIELD:
        inputSummary.inputs.push(... this.bmpYield.getSummaryInputs([...path, 'bmpYield']));
        break;
      case FeedstockBmpType.COD:
        inputSummary.inputs.push(... this.bmpCod.getSummaryInputs([...path, 'bmpCod']));
        break;
      case FeedstockBmpType.NUTRIENT_COMPOSITION:
        inputSummary.inputs.push(... this.bmpNutrient.getSummaryInputs([...path, 'bmpNutrient']));
        break;
      case FeedstockBmpType.ELEMENTAL:
        inputSummary.inputs.push(... this.bmpElemental.getSummaryInputs([...path, 'bmpElemental']));
        break;
      case FeedstockBmpType.AI1:
        inputSummary.inputs.push(... this.bmpAI1.getSummaryInputs([...path, 'bmpAI1']));
        break;
      case FeedstockBmpType.AI2:
        inputSummary.inputs.push(... this.bmpAI2.getSummaryInputs([...path, 'bmpAI2']));
        break;
    }
    return inputSummary;
  }

  /**
   * Returns an array of InputSummaryItem containing inputs for each member of the FeedstockBmpPotential class
   */
  getSummaryInputs(path: string[]) {
    let inputs: InputSummaryItem[] = [];
    inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialPropName.type, this.type, [...path, 'type']);
    return inputs;
  }
}

/**
 * An enum that contains a proper display name for properties of the FeedstockBmpPotentialCod class.
 *
 * ALL MEMBERS ARE INTENTIONALLY CAMELCASE FOR OBJ REFERENCING
 */
export enum FeedstockBmpPotentialPropName {
  type = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.TYPE',
  bmpDirect = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.BMP_DIRECT.TITLE',
  bmpCurve = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.BMP_CURVE.TITLE',
  yield = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.YIELD.TITLE',
  cod = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.COD.TITLE',
  nutrient = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.NUTRIENT.TITLE',
  elemental = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.ELEMENTAL.TITLE',
  AI1 = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.AI2.TITLE',
  AI2 = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.AI2.TITLE',
}

/**
 * An enum that contains members each feedstock bmp type
 */
export enum FeedstockBmpType {
  BMP_DIRECT = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.TYPES.BMP',
  BMP_CURVE = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.TYPES.BC',
  BIOGAS_YIELD = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.TYPES.BY',
  COD = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.TYPES.COD',
  NUTRIENT_COMPOSITION = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.TYPES.NUT',
  ELEMENTAL = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.TYPES.EA',
  AI1 = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.TYPES.AI1',
  AI2 = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.TYPES.AI2',
}
